import { lazy } from 'react';

import {
  ProtectedAddCompany,
  ProtectedAddContract,
  ProtectedAdmins,
  ProtectedBilling,
  ProtectedClaimCategorization,
  ProtectedClaimEligibilityCheck,
  ProtectedClaimMakeDecision,
  ProtectedClaims,
  ProtectedClaimsTool,
  ProtectedClaimViewer,
  ProtectedCompanies,
  ProtectedConsultations,
  ProtectedContract,
  ProtectedInvoiceTraining,
  ProtectedAIPipeline,
  ProtectedMember,
  ProtectedResources,
  ProtectedServiceProvider,
  ProtectedStats,
  ProtectedViewCompany,
  ProtectedWallets,
  ProtectedWalletTransactions,
} from './guards';

// Lazily-loaded page components. Each becomes its own bundle chunk so the
// initial load no longer pulls in every page up front.
const AdminSettings = lazy(() => import('../pages/Admins/AdminSetings'));
const Navigator = lazy(() => import('../pages/Admins/Navigator'));
const ProductConfiguration = lazy(
  () => import('../pages/Admins/ProductConfiguration'),
);
const UserPermissions = lazy(() => import('../pages/Admins/UserPermissions'));
const AnnexesPage = lazy(() => import('../pages/Annexes'));
const ClaimDetails = lazy(() => import('../pages/Claims/ClaimDetails'));
const PCloud = lazy(() => import('../pages/Claims/PCloud'));
const Reporting = lazy(() => import('../pages/Claims/Reporting'));
const ViewBenefitsPlanDetails = lazy(
  () => import('../pages/Crawford/ViewBenefitsPlanDetails'),
);
const BenefitsPlan = lazy(() => import('../pages/Customers/BenefitsPlan'));
const BudgetDetailView = lazy(
  () =>
    import('../pages/Customers/BenefitsPlan/ViewBenefitPlan/BudgetDetailView'),
);
const EligibleTreatmentView = lazy(
  () =>
    import('../pages/Customers/BenefitsPlan/ViewBenefitPlan/EligibleTreatmentView'),
);
const EmployeeEligibilityView = lazy(
  () =>
    import('../pages/Customers/BenefitsPlan/ViewBenefitPlan/EmployeeEligibilityView'),
);
const EntitiesView = lazy(
  () => import('../pages/Customers/BenefitsPlan/ViewBenefitPlan/EntitiesView'),
);
const TaxationView = lazy(
  () => import('../pages/Customers/BenefitsPlan/ViewBenefitPlan/TaxationView'),
);
const ViewBenefitsPlan = lazy(
  () => import('../pages/Customers/BenefitsPlan/ViewBenefitsPlan'),
);
const ViewBillingContract = lazy(
  () => import('../pages/Customers/Billing/ViewBillingContract'),
);
const ViewHeadCountHistory = lazy(
  () => import('../pages/Customers/Billing/ViewHeadcountHistory'),
);
const ViewContract = lazy(
  () => import('../pages/Customers/Contracts/ViewContract'),
);
const ErrorPage = lazy(() => import('../pages/Error'));
const ForgotPassword = lazy(() => import('../pages/ForgotPassword'));
const HomePage = lazy(() => import('../pages/Home'));
const LoginPage = lazy(() => import('../pages/Login'));
const LogoutPage = lazy(() => import('../pages/Logout'));
const Clinics = lazy(() => import('../pages/Members/Clinics'));
const LogRecord = lazy(() => import('../pages/Members/Consultation/LogRecord'));
const ViewRecord = lazy(
  () => import('../pages/Members/Consultation/ViewRecord'),
);
const MemberDetails = lazy(
  () => import('../pages/Members/Members/MemberDetails'),
);
const TreatmentsPage = lazy(() => import('../pages/NavigatorL0'));
const NavigatorL1Page = lazy(() => import('../pages/NavigatorL1'));
const ResetPassword = lazy(() => import('../pages/ResetPassword'));
const SettingsPage = lazy(() => import('../pages/Settings'));
const TransactionDetails = lazy(
  () => import('../pages/Wallets/Transactions/TransactionDetails'),
);
const ViewTransactionDetails = lazy(
  () => import('../pages/Wallets/Transactions/ViewTransactionDetails'),
);
const WalletDetail = lazy(() => import('../pages/Wallets/WalletDetailsScreen'));

// Order matters: kept identical to the original <Switch> definition so route
// matching behaviour is unchanged.
const routes = [
  { path: '/login', component: LoginPage },
  { path: '/logout', component: LogoutPage },
  { path: '/recover-password', component: ForgotPassword },
  { path: '/reset-password/:uid/:token', component: ResetPassword },
  { path: '/', component: HomePage },

  { path: '/billing', component: ProtectedBilling },
  { path: '/annexes/*?', component: AnnexesPage },
  { path: '/billing/:contractName', component: ViewBillingContract },
  {
    path: '/billing/:contractName/headcountHistory',
    component: ViewHeadCountHistory,
  },
  { path: '/companies', component: ProtectedCompanies },
  { path: '/companies/add-company', component: ProtectedAddCompany },
  { path: '/companies/:companyName', component: ProtectedViewCompany },
  {
    path: '/companies/:companyName/benefitPlan',
    component: ViewBenefitsPlanDetails,
  },
  {
    path: '/companies/:companyName/benefitPlan/entities',
    component: EntitiesView,
  },
  {
    path: '/companies/:companyName/benefitPlan/budget',
    component: BudgetDetailView,
  },
  {
    path: '/companies/:companyName/benefitPlan/eligibletreatment',
    component: EligibleTreatmentView,
  },
  {
    path: '/companies/:contractName/benefitPlan/employeeeligibility',
    component: EmployeeEligibilityView,
  },
  {
    path: '/companies/:contractName/benefitPlan/taxation',
    component: TaxationView,
  },

  { path: '/contracts', component: ProtectedContract },
  { path: '/contracts/add-contract', component: ProtectedAddContract },
  { path: '/contracts/:contractName/benefitsPlan', component: BenefitsPlan },
  {
    path: '/contracts/:contractName/benefitPlan',
    component: ViewBenefitsPlan,
  },
  {
    path: '/contracts/:contractName/benefitPlan/entities',
    component: EntitiesView,
  },
  {
    path: '/contracts/:contractName/benefitPlan/budget',
    component: BudgetDetailView,
  },
  {
    path: '/contracts/:contractName/benefitPlan/employeeeligibility',
    component: EmployeeEligibilityView,
  },
  {
    path: '/contracts/:contractName/benefitPlan/taxation',
    component: TaxationView,
  },
  {
    path: '/contracts/:contractName/benefitPlan/eligibletreatment',
    component: EligibleTreatmentView,
  },
  { path: '/contracts/:contractName', component: ViewContract },
  { path: '/claims', component: ProtectedClaims },
  { path: '/claims-tool', component: ProtectedClaimsTool },
  { path: '/claims-tool/page/:page', component: ProtectedClaimsTool },
  {
    path: '/claims-tool/claim/:claim_id/member/:member_id/eligibility-check',
    component: ProtectedClaimEligibilityCheck,
  },
  {
    path: '/claims-tool/claim/:claim_id/eligibility-check',
    component: ProtectedClaimEligibilityCheck,
  },
  {
    path: '/claims-tool/claim/:claim_id/member/:member_id/categorization',
    component: ProtectedClaimCategorization,
  },
  {
    path: '/claims-tool/claim/:claim_id/categorization',
    component: ProtectedClaimCategorization,
  },
  {
    path: '/claims-tool/claim/:claim_id/member/:member_id/decision',
    component: ProtectedClaimMakeDecision,
  },
  {
    path: '/claims-tool/claim/:claim_id/decision',
    component: ProtectedClaimMakeDecision,
  },
  {
    path: '/claims-tool/claim/:claim_id/member/:member_id',
    component: ProtectedClaimViewer,
  },
  { path: '/claims-tool/claim/:claim_id', component: ProtectedClaimViewer },
  { path: '/claims/:id', component: ClaimDetails },
  { path: '/wallets', component: ProtectedWallets },
  { path: '/wallets/:id', component: WalletDetail },

  { path: '/wallet-transactions', component: ProtectedWalletTransactions },
  { path: '/wallet-transactions/:id', component: TransactionDetails },
  {
    path: '/wallet-transactions/view/:id',
    component: ViewTransactionDetails,
  },

  { path: '/pcloud', component: PCloud },
  { path: '/reporting', component: Reporting },
  { path: '/members', component: ProtectedMember },
  { path: '/members/:memberName', component: MemberDetails },
  { path: '/consultations', component: ProtectedConsultations },
  { path: '/consultations/past', component: ProtectedConsultations },
  { path: '/consultations/:memberName', component: MemberDetails },
  { path: '/consultations/past/:memberName', component: MemberDetails },
  { path: '/consultations/:memberName/logRecord', component: LogRecord },
  { path: '/consultations/past/:memberName/logRecord', component: LogRecord },
  { path: '/consultations/:memberName/viewRecord', component: ViewRecord },
  {
    path: '/consultations/past/:memberName/viewRecord',
    component: ViewRecord,
  },
  { path: '/consultations/:memberName/editRecord', component: LogRecord },
  {
    path: '/consultations/past/:memberName/editRecord',
    component: LogRecord,
  },
  { path: '/clinics', component: Clinics },
  { path: '/treatments', component: TreatmentsPage },
  { path: '/resources', component: ProtectedResources },
  { path: '/navigator', component: Navigator },
  { path: '/navigator-one/*?', component: NavigatorL1Page },
  { path: '/admins', component: ProtectedAdmins },
  { path: '/admin-settings', component: AdminSettings },
  { path: '/stats', component: ProtectedStats },
  { path: '/service-providers', component: ProtectedServiceProvider },
  { path: '/product-configuration', component: ProductConfiguration },
  { path: '/user-permissions', component: UserPermissions },
  { path: '/settings', component: SettingsPage },
  { path: '/invoice-training', component: ProtectedInvoiceTraining },
  { path: '/ai-pipelines', component: ProtectedAIPipeline },

  { path: '/error', component: ErrorPage },
];

export default routes;
